import "./DropDownMenu.css"

import { useState, useEffect } from "react"
import debounce from "lodash.debounce"
import classNames from "classnames"
import OutsideClickHandler from "react-outside-click-handler"

import { getPageOffsets, getWindowWidth } from "web/services/browser"

const OFFSET_EDGE_PADDING = 20
const FULL_SCREEN_BREAKPOINT = 768

export default function DropDownMenu({
  onClose,
  triggerElementId,
  children,
  transition = true,
  forceAnchorRight = false,
  disableFullScreenMobile = false,
}) {
  const windowWidth = getWindowWidth()

  const fullScreenDropDown =
    windowWidth < FULL_SCREEN_BREAKPOINT && !disableFullScreenMobile
  const shouldTransition = fullScreenDropDown ? false : transition
  const [slidDown, setSlidDown] = useState(false)

  function calculatePosition() {
    const triggerElement = document.getElementById(triggerElementId)
    if (!triggerElement) return {}

    const { right, left, top, height, scrollY, scrollX } =
      getPageOffsets(triggerElement)

    // We calculate which side of the trigger element to anchor too,
    // by default we use the left unless the left of the target is on the right side of the page
    // Anchor left -> float right
    // Anchor right -> float left
    const shouldAnchorOnElementRight =
      left > windowWidth / 2 || forceAnchorRight

    // Offset is applied to stop our larger modal from going off screen
    let offset = 0
    if (shouldAnchorOnElementRight) {
      const largeContentLeftEdge =
        right - OFFSET_EDGE_PADDING - FULL_SCREEN_BREAKPOINT
      if (largeContentLeftEdge < 0) {
        offset = largeContentLeftEdge
      }
    } else {
      const largeContentRightEdge =
        left + OFFSET_EDGE_PADDING + FULL_SCREEN_BREAKPOINT
      if (largeContentRightEdge > windowWidth) {
        offset = windowWidth - largeContentRightEdge
      }
    }

    const position = {}

    position.top = `${top - scrollY + height + 5}px`
    if (shouldAnchorOnElementRight) {
      position.right = `${windowWidth + offset - right - scrollX}px`
    } else {
      position.left = `${left + offset - scrollX}px`
    }

    return position
  }

  useEffect(() => {
    setSlidDown(true)
  }, [])

  useEffect(() => {
    function closeOnEscape(event) {
      if (event.key === "Escape") onClose()
    }

    const closeOnScrollOrResize = debounce(
      () => {
        onClose()
      },
      50,
      { leading: true }
    )

    window.addEventListener("keydown", closeOnEscape)
    window.addEventListener("resize", closeOnScrollOrResize)

    if (!fullScreenDropDown) {
      window.addEventListener("scroll", closeOnScrollOrResize)
    }

    return () => {
      window.removeEventListener("keydown", closeOnEscape)
      window.removeEventListener("resize", closeOnScrollOrResize)

      if (!fullScreenDropDown) {
        window.removeEventListener("scroll", closeOnScrollOrResize)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dropdownClassNames = classNames({
    "drop-down-menu outline-none md:rounded": true,
    transition: shouldTransition,
    "slid-down": slidDown,
    "w-full h-full inset-0": fullScreenDropDown,
  })

  let style = {}
  if (!fullScreenDropDown) {
    style = {
      ...calculatePosition(),
    }
  }

  return (
    <div className={dropdownClassNames} style={style}>
      <OutsideClickHandler onOutsideClick={onClose} display="contents">
        {children}
      </OutsideClickHandler>
    </div>
  )
}
